/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { breakpoints } from '@coursera/cds-core';

import type { HeroBannerProps } from 'bundles/degree-description/components/modules/hero/HeroBanner';
import { useDegreeDescriptionPageElementRefs } from 'bundles/degree-description/contexts/DegreeDescriptionPageElementRefsContext';

type Props = {
  heroBanner: React.ReactElement<HeroBannerProps>;
  informationalSection: React.ReactElement;
  /**
   *  contentInBetween: renderProps for content stays under hero image and above informational section
   */
  contentInBetween?: React.ReactElement;
};

const styles = {
  container: css`
    display: grid;
    grid-template-areas: 'banner banner banner' 'content content content' 'info info info';
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: auto auto 1fr;

    ${breakpoints.up('lg')} {
      grid-template-columns: 1fr minmax(1440px, 1fr) 1fr;
    }
  `,
  banner: css`
    grid-area: banner;
    ${breakpoints.up('md')} {
      padding-bottom: var(--cds-spacing-600);
    }
    ${breakpoints.down('sm')} {
      padding-bottom: var(--cds-spacing-300);
    }
  `,
  info: css`
    grid-area: info;
    padding-bottom: var(--cds-spacing-600);
    ${breakpoints.down('sm')} {
      padding-bottom: 0;
    }
  `,
  contentInBetween: css`
    grid-area: content;
    position: relative;
    ${breakpoints.down('sm')} {
      margin-top: calc(var(--cds-spacing-300) * -1);
      top: 0;
      padding-bottom: var(--cds-spacing-200);
    }
  `,
};

const HeroModule = ({ heroBanner, informationalSection, contentInBetween }: Props) => {
  const { setHeroBannerRef } = useDegreeDescriptionPageElementRefs();

  return (
    <div css={styles.container}>
      <div
        className="HeroModuleBanner"
        css={styles.banner}
        ref={(e) => {
          setHeroBannerRef?.(e as HTMLElement);
        }}
      >
        {heroBanner}
      </div>

      {contentInBetween && <div css={styles.contentInBetween}>{contentInBetween}</div>}

      <div css={styles.info}>{informationalSection}</div>
    </div>
  );
};

export default HeroModule;
